import React, { useEffect, useState } from "react";

import { useBottomMenu } from "../../../hooks/useBottomMenu";
import { useLabels } from "../../../hooks/useLabels";
import { IBottomMenuButton } from "../../bottom-menu/buttons";
import { labels as backgroundLabels } from "./labels";

import { useHasBottomRootMenu } from "../../../hooks/useHasBottomRootMenu";
import { useSetColorBackground } from "../background-color-panel/hooks/useSetColorBackground";
import { useSetPatternBackground } from "../background-pattern-panel/hooks/useSetPatternBackground";

import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import {
	BACKGROUNDS_SUB_MENU_ID,
	BottomMenuIds,
	BottomMenuPaths,
	makeBackgroundMenu,
} from "../../bottom-menu/paths";
import { useBackgroundPatterns } from "../background-pattern-panel/hooks/useBackgroundPatterns";
import { BottomBackgroundMenu } from "./bottom-background-menu";

export const BottomBackgroundMenuContainer = () => {
	const { gotoPath, currentPath, stack } = useBottomMenu();
	const hasRootMenu = useHasBottomRootMenu();
	const googleAnalytics = useGoogleAnalytics();

	const [activeButton, setActiveButton] = useState("");
	const backgroundPatterns = useBackgroundPatterns();
	const [[colorTitle, patternsTitle], isLoadingLabels] = useLabels([
		backgroundLabels.colors,
		backgroundLabels.patterns,
	]);

	const canChangeColorBackgrounds = !!useSetColorBackground();
	const canChangePatternBackgrounds = !!useSetPatternBackground();
	const canChangeBackground = canChangeColorBackgrounds || canChangePatternBackgrounds;

	const menuItems: IBottomMenuButton<BACKGROUNDS_SUB_MENU_ID | "back">[] = [];

	const onMenuClick = (menuId: BACKGROUNDS_SUB_MENU_ID) => {
		const isAlreadyOpen = currentPath
			? currentPath.startsWith(makeBackgroundMenu(menuId))
			: false;
		if (isAlreadyOpen) {
			gotoPath(BottomMenuPaths.Backgrounds);
		} else if (menuId === BottomMenuIds.BackgroundColor) {
			googleAnalytics.trackEventToGa({
				trackingAction: "BackgroundColorClicked",
			});
			gotoPath(BottomMenuPaths.BackgroundColor);
		} else if (menuId === BottomMenuIds.BackgroundPattern) {
			googleAnalytics.trackEventToGa({
				trackingAction: "BackgroundPatternClicked",
			});
			gotoPath(BottomMenuPaths.BackgroundPattern);
		}
	};

	menuItems.push({
		id: BottomMenuIds.BackgroundColor,
		iconName: "paint-background-color",
		title: colorTitle,
		onClick: () => {
			onMenuClick(BottomMenuIds.BackgroundColor);
		},
	});

	menuItems.push({
		id: BottomMenuIds.BackgroundPattern,
		iconName: "paint-background-pattern",
		isDisabled: !(backgroundPatterns.length > 0),
		title: patternsTitle,
		onClick: () => {
			onMenuClick(BottomMenuIds.BackgroundPattern);
		},
	});

	useEffect(() => {
		const thisMenuIndex = stack.findIndex((item) => item.id === BottomMenuIds.Backgrounds);
		const nextMenu = thisMenuIndex >= 0 ? stack[thisMenuIndex + 1] : undefined;
		const nextMenuId = nextMenu?.id ?? "";
		setActiveButton(nextMenuId);
	}, [stack]);

	useEffect(() => {
		if (!canChangeBackground) {
			gotoPath(BottomMenuIds.Root);
		}
	}, [canChangeBackground, gotoPath]);

	if (isLoadingLabels) return null;

	return menuItems.length > 0 ? (
		<BottomBackgroundMenu
			onBack={() => gotoPath(BottomMenuPaths.Root)}
			showBackButton={hasRootMenu}
			menuItems={menuItems}
			activeButtonId={activeButton}
		/>
	) : null;
};
